import { Amplify } from 'aws-amplify';
import { uploadData } from "aws-amplify/storage";
import { post } from 'aws-amplify/api';
import { Buffer } from 'buffer';

// Define expected API response structure
interface ApiResponseData {
  image?: string;
  error?: string;
  // Add other potential properties from your Lambda if needed
}

export interface UploadResponse {
  success: boolean;
  url?: string;
  bucketName?: string;
  error?: string;
}

export interface MinecraftMeResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

export async function uploadImage(
  imageData: Blob | { base64: string; mimeType?: string },
  contentType: string | undefined,
  fileExtension: string
): Promise<UploadResponse> {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const filePath = `minecraft-me/${timestamp}-${randomString}.${fileExtension}`;

  const currentConfig = Amplify.getConfig();
  const bucketName = currentConfig.Storage?.S3?.buckets?.MinecraftMe?.bucketName;
  const region = currentConfig.Storage?.S3?.buckets?.MinecraftMe?.region;

  if (!bucketName || !region) {
    return {
      success: false,
      error: 'Storage configuration not found. Ensure Amplify is configured in App.tsx.'
    };
  }

  try {
    let dataToUpload: Blob | ArrayBuffer;

    if (imageData instanceof Blob) {
      dataToUpload = imageData;
    } else {
      const nodeBuffer = Buffer.from(imageData.base64, 'base64');
      dataToUpload = nodeBuffer.buffer.slice(
         nodeBuffer.byteOffset, nodeBuffer.byteOffset + nodeBuffer.byteLength
      );
      contentType = contentType || imageData.mimeType;
    }

    const result = await uploadData({
      path: filePath,
      data: dataToUpload,
      options: {
        contentType: contentType,
      }
    }).result;

    return {
      success: true,
      url: result.path,
      bucketName
    };
  } catch (error: any) {
    console.error("UploadData Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed in services.ts'
    };
  }
}

export async function processImage(bucketName: string, imageKey: string): Promise<MinecraftMeResponse> {
  try {
    const restOperation = post({
      apiName: 'minecraftme',
      path: '/process',
      options: {
         body: {
            bucket: bucketName,
            imageKey: imageKey
         }
      }
    });

    const { body } = await restOperation.response;
    // Assert the type after parsing JSON
    const data = await body.json() as ApiResponseData | null; 

    // Check if data is an object and has the expected properties
    if (data && typeof data === 'object' && data.image) { 
      return {
        success: true,
        imageUrl: data.image
      };
    } else {
      // Extract error message safely
      const errorMessage = (data && typeof data === 'object' && data.error) 
                            ? data.error 
                            : 'API call succeeded but response format was unexpected.';
      return {
        success: false,
        error: errorMessage
      };
    }
  } catch (error: any) {
    console.error("API Error:", error);
    let errorMessage = 'Failed to call API';
    try {
      if (error.response && error.response.body) {
        const errorBody = await error.response.body.json() as unknown; // Parse as unknown first

        // Check if it's an object and then check for specific properties
        if (typeof errorBody === 'object' && errorBody !== null) {
          if ('error' in errorBody && typeof errorBody.error === 'string') {
            errorMessage = errorBody.error;
          } else if ('message' in errorBody && typeof errorBody.message === 'string') { 
            errorMessage = errorBody.message;
          } else {
             // Fallback if structure is unexpected
             errorMessage = JSON.stringify(errorBody);
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
    } catch (parseError) {
       console.warn("Could not parse error response body:", parseError);
    }
    return {
      success: false,
      error: errorMessage
    };
  }
} 